import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'prysh_session_token';

// Create axios instance with credentials
const authApi = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Attach token from localStorage as Authorization header (Safari/iOS fallback)
authApi.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Global interceptor — ensures ALL axios calls (not just authApi) include the token
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [creditsBalance, setCreditsBalance] = useState(0);

  const refreshCredits = useCallback(async () => {
    try {
      const res = await authApi.get('/api/credits/balance');
      setCreditsBalance(res.data.balance);
    } catch (err) {
      // silent
    }
  }, []);

  // Check auth status on mount
  const checkAuth = useCallback(async () => {
    try {
      const response = await authApi.get('/api/auth/me');
      if (response.data) {
        setUser(response.data);
        setIsAuthenticated(true);
        setCreditsBalance(response.data.credits_balance || 0);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const response = await authApi.post('/api/auth/login', { email, password });
      if (response.data.session_token) {
        localStorage.setItem(TOKEN_KEY, response.data.session_token);
      }
      setUser(response.data.user);
      setIsAuthenticated(true);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      throw new Error(message);
    }
  };

  const signup = async (email, password, name) => {
    try {
      const response = await authApi.post('/api/auth/signup', { email, password, name });
      if (window.fbq) window.fbq('track', 'CompleteRegistration');
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Signup failed';
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      await authApi.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setIsAuthenticated(false);
  };

  const googleLogin = async () => {
    // Get Google Client ID from backend
    try {
      const response = await authApi.get('/api/auth/google/client-id');
      const clientId = response.data.client_id;
      
      const redirectUri = window.location.origin + '/auth/callback';
      const scope = 'email profile';
      const responseType = 'code';
      
      const googleAuthUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${clientId}&` +
        `redirect_uri=${encodeURIComponent(redirectUri)}&` +
        `response_type=${responseType}&` +
        `scope=${encodeURIComponent(scope)}&` +
        `access_type=offline&` +
        `prompt=consent`;
      
      window.location.href = googleAuthUrl;
    } catch (error) {
      console.error('Failed to start Google login:', error);
    }
  };

  const processGoogleCallback = async (code) => {
    const redirectUri = window.location.origin + '/auth/callback';
    
    try {
      const response = await authApi.post('/api/auth/google/callback', { 
        code, 
        redirect_uri: redirectUri 
      });
      
      if (response.data.session_token) {
        localStorage.setItem(TOKEN_KEY, response.data.session_token);
      }
      setUser(response.data.user);
      setIsAuthenticated(true);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.detail || 'Google login failed';
      throw new Error(message);
    }
  };

  // Keep old function for backwards compatibility but redirect to new one
  const processGoogleSession = async (sessionId) => {
    throw new Error('Please use Google Sign-In button to login');
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    signup,
    logout,
    googleLogin,
    processGoogleCallback,
    processGoogleSession,
    checkAuth,
    refreshCredits,
    creditsBalance,
    // Role helpers
    isAdmin: user?.role === 'admin',
    isPro: user?.role === 'pro' || user?.role === 'admin',
    isBeginner: user?.role === 'beginner' || !user?.role,
    userRole: user?.role || 'beginner'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
